export const tutorialStepOrder = ['how-it-works', 'mapping', 'thresholds', 'invite'] as const;

export type TutorialStepId = (typeof tutorialStepOrder)[number];

export type StoredPostOnboardingTutorialState = {
  seenAt: string | null;
  dismissedAt: string | null;
  completedAt: string | null;
  currentStepId: TutorialStepId;
};

const STORAGE_PREFIX = 'flowqa-post-onboarding-tutorial';

const buildStorageKey = (userId: string, organisationId: string, projectId: string): string =>
  `${STORAGE_PREFIX}:${userId}:${organisationId}:${projectId}`;

const isTutorialStepId = (value: unknown): value is TutorialStepId =>
  typeof value === 'string' && tutorialStepOrder.includes(value as TutorialStepId);

export const getDefaultPostOnboardingTutorialState = (): StoredPostOnboardingTutorialState => ({
  seenAt: null,
  dismissedAt: null,
  completedAt: null,
  currentStepId: 'how-it-works',
});

export const readPostOnboardingTutorialState = (
  userId: string,
  organisationId: string,
  projectId: string,
): StoredPostOnboardingTutorialState | null => {
  if (typeof window === 'undefined') return null;
  const rawValue = window.localStorage.getItem(buildStorageKey(userId, organisationId, projectId));
  if (rawValue === null) return null;

  try {
    const parsed = JSON.parse(rawValue) as Partial<StoredPostOnboardingTutorialState>;
    return {
      seenAt: typeof parsed.seenAt === 'string' ? parsed.seenAt : null,
      dismissedAt: typeof parsed.dismissedAt === 'string' ? parsed.dismissedAt : null,
      completedAt: typeof parsed.completedAt === 'string' ? parsed.completedAt : null,
      currentStepId: isTutorialStepId(parsed.currentStepId) ? parsed.currentStepId : 'how-it-works',
    };
  } catch {
    return null;
  }
};

export const writePostOnboardingTutorialState = (
  userId: string,
  organisationId: string,
  projectId: string,
  state: StoredPostOnboardingTutorialState,
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildStorageKey(userId, organisationId, projectId),
    JSON.stringify(state),
  );
};
